import { Component, ChangeDetectionStrategy, AfterContentInit, ContentChildren, EventEmitter, Input, Output, QueryList, HostBinding } from '@angular/core';
import { startWith, Observable, switchMap, merge, filter } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BaseControlComponent } from '@knorr-bremse-portals/ngx-components/internal/controls';
import { FilterChipComponent } from './filter-chip/filter-chip.component';

@Component({
  selector: 'kb-chip-set[type="filter"]',
  templateUrl: './chip-set-filter.component.html',
  styleUrls: ['./chip-set-filter.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [],
})
export class ChipSetFilterComponent extends BaseControlComponent<string> implements AfterContentInit {

  @HostBinding('class.kb-scrollbar') private readonly classKbScrollbar = true;

  @Input()
  public get value(): string { return this._value; }
  public set value(newValue: string) {
    this._value = newValue;
    this.selectOption(this._value);
  }
  private _value!: string;
  @Output() public readonly valueChange = new EventEmitter<string>();

  @ContentChildren(FilterChipComponent, { descendants: true }) private options!: QueryList<FilterChipComponent>;

  /** `View -> model callback called when value changes` */
  private onChange: (value: string) => void = () => undefined;

  /** `View -> model callback called when select has been touched` */
  private onTouched: () => void = () => undefined;

  private afterContentInit = false;
  private valueBeforeContentInit = this.value;

  public ngAfterContentInit(): void {
    this.afterContentInit = true;
    if (this.valueBeforeContentInit !== this.value) {
      this.value = this.valueBeforeContentInit;
    }

    this.setupOptionsChangesHandler();
    this.setupOptionsCheckedChangeHandler();
    this.setupOptionsTouchedChangeHandler();
  }

  public selectOption(value: string) {
    const option = this.options.find(option =>  option.value === value);
    this.options.forEach(option => option.checked = false);
    if (option) {
      option.checked = true;
    }
  }

  private setupOptionsChangesHandler() {
    this.options.changes.pipe(
      startWith(this.options),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => {
      this.updateOptionsName();
    });
  }

  protected override nameUpdated(): void {
    this.updateOptionsName();
  }

  private updateOptionsName() {
    if (!this.options) {
      return;
    }

    this.options.forEach((option) => {
      option.name = this.name;
    });
  }

  private setupOptionsCheckedChangeHandler() {
    (this.options.changes as Observable<QueryList<FilterChipComponent>>).pipe(
      startWith(this.options),
      switchMap(() => merge(...this.options.map(option => option.checkedChange))),
      filter((option) => option.checked === true),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((selectedOption) => {
      this._value = selectedOption.value;
      this.valueChange.emit(this.value);
      this.onChange(this.value);
      this.ref.markForCheck(); // update super.showError
    });
  }

  private setupOptionsTouchedChangeHandler() {
    (this.options.changes as Observable<QueryList<FilterChipComponent>>).pipe(
      startWith(this.options),
      switchMap(() => merge(...this.options.map(option => option.touchedChange))),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => {
      this.onTouched();
      this.ref.markForCheck(); // update super.showError
    });
  }

  public writeValue(value: string): void {
    if (!this.afterContentInit) {
      this.valueBeforeContentInit = value;
    } else {
      this.value = value;
    }
  }
  public registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }
  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

}
